import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, CheckCircle, Globe, BarChart4, ShieldCheck } from 'lucide-react';
import Button from '../components/common/Button';
import Layout from '../components/layout/Layout';
import { useTestStore } from '../store/testStore';
import UserDetailsForm from '../components/test/UserDetailsForm';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const { startTest, resetTest, hasCompletedOnboarding } = useTestStore();
  
  const handleStartTest = () => {
    startTest();
    navigate('/test');
  };
  
  const features = [
    {
      icon: <Globe className="h-6 w-6 text-primary-600" />,
      title: 'Comprehensive Assessment',
      description: 'Evaluate your export readiness across 10 critical business areas with 200 targeted questions.'
    },
    {
      icon: <BarChart4 className="h-6 w-6 text-primary-600" />,
      title: 'Detailed Analytics',
      description: 'Receive a complete breakdown of your strengths and areas for improvement with actionable insights.'
    },
    {
      icon: <ShieldCheck className="h-6 w-6 text-primary-600" />,
      title: 'Expert Recommendations',
      description: 'Get tailored recommendations to improve your export readiness based on industry best practices.'
    }
  ];
  
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };
  
  // Show UserDetailsForm if onboarding is not completed
  if (!hasCompletedOnboarding) {
    return <UserDetailsForm />;
  }
  
  return (
    <Layout>
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-primary-50 to-white py-16 md:py-24">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Evaluate Your <span className="text-primary-600">Export Readiness</span> in 60 Minutes
              </h1>
              
              <p className="text-lg text-gray-600 mb-8">
                Take our comprehensive assessment to determine if your business is ready for 
                international export. Identify strengths and areas for improvement across 10 
                critical export categories.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  variant="primary" 
                  size="lg" 
                  rightIcon={<ArrowRight />}
                  onClick={handleStartTest}
                >
                  Start Test Now
                </Button>
                
                <Button 
                  variant="outline" 
                  size="lg"
                  onClick={() => {
                    const featuresSection = document.getElementById('features');
                    if (featuresSection) {
                      featuresSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  Learn More
                </Button>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white p-6 rounded-xl shadow-card">
                <div className="bg-primary-50 p-4 rounded-lg mb-4">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-bold text-gray-800">Export Readiness Score</h3>
                    <span className="text-primary-600 font-bold">78%</span>
                  </div>
                  
                  <div className="w-full bg-gray-200 h-3 rounded-full">
                    <motion.div
                      className="bg-primary-600 h-full rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: '78%' }}
                      transition={{ duration: 1, delay: 0.8 }}
                    />
                  </div>
                </div>
                
                <ul className="space-y-3">
                  {['Financial Readiness', 'Market Knowledge', 'Export Documentation', 'Risk Management'].map((item, index) => (
                    <motion.li 
                      key={item}
                      className="flex items-center"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.5 + (index * 0.1) }}
                    >
                      <CheckCircle className="h-5 w-5 text-success-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
              
              <div className="absolute -top-4 -right-4 bg-accent-500 text-white text-sm font-bold py-2 px-4 rounded-full shadow-lg">
                Sample Report
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section id="features" className="py-16 bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Comprehensive Export Readiness Assessment
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our test evaluates your business across all critical aspects of export operations,
              providing detailed insights and actionable recommendations.
            </p>
          </motion.div>
          
          <motion.div 
            className="grid grid-cols-1 md:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-xl shadow-card hover:shadow-lg transition-shadow"
                variants={itemVariants}
              >
                <div className="bg-primary-50 p-3 rounded-full inline-block mb-4">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* Categories Section */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              10 Critical Export Readiness Categories
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Our assessment evaluates your business across these essential areas to determine your export readiness.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Export Finance', description: 'Capital requirements, payment terms, and financial risk management' },
              { title: 'Compliance', description: 'Understanding and meeting export control regulations and requirements' },
              { title: 'Export Documentation', description: 'Proper preparation and management of required export documents' },
              { title: 'Market Knowledge', description: 'Understanding target markets, competition, and customer needs' },
              { title: 'Export Business Plan', description: 'Strategic planning and resource allocation for export activities' },
              { title: 'Product Readiness', description: 'Product adaptation, packaging, and certification for international markets' },
              { title: 'Digital Readiness', description: 'E-commerce capabilities, online presence, and digital marketing' },
              { title: 'Export Logistics', description: 'Transportation, distribution, and supply chain management' },
              { title: 'Legal Framework', description: 'International contracts, intellectual property, and dispute resolution' },
              { title: 'Risk Management', description: 'Identifying, assessing, and mitigating export-related risks' }
            ].map((category, index) => (
              <motion.div
                key={index}
                className="bg-white p-6 rounded-xl shadow-card"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
              >
                <h3 className="text-lg font-bold text-gray-800 mb-2">{category.title}</h3>
                <p className="text-sm text-gray-600">{category.description}</p>
              </motion.div>
            ))}
          </div>
          
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.5 }}
          >
            <Button 
              variant="primary" 
              size="lg" 
              onClick={handleStartTest}
              rightIcon={<ArrowRight />}
            >
              Start Your Assessment
            </Button>
          </motion.div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-16 bg-primary-600 text-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h2
              className="text-3xl font-bold mb-6"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              Ready to Evaluate Your Export Potential?
            </motion.h2>
            
            <motion.p
              className="text-lg opacity-90 mb-8"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
            >
              Take our comprehensive 60-minute assessment and get actionable insights 
              to improve your export readiness. The test is free and you'll receive 
              a detailed report immediately.
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
            >
              <Button 
                onClick={handleStartTest}
                className="bg-white text-primary-700 hover:bg-gray-100 focus:ring-white"
                size="lg"
                rightIcon={<ArrowRight />}
              >
                Begin Your Export Readiness Test
              </Button>
            </motion.div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LandingPage;